import type { SourceType } from '../typings/Integration.interface';

/**
 * Mapping between Hookdeck Console "provider" keys (lowercase, as served by
 * samples.hookdeck.com/providers.json) and Source Types.
 *
 * Used by the Console's **webhook samples list** to bridge the providers.json provider key and
 * a source's Source Type (so the sample for a source's type can be auto-selected). The
 * test-URL API itself does NOT use this — it takes a Source Type directly. It references the
 * canonical SourceType union but does not move or modify the Source Type definitions.
 *
 * Inspect uses it too, through `sourceTypeForSamplesProvider` below: each provider in the samples
 * feed saves as the Source Type it names here or, when it isn't listed, as the type spelled like
 * its key.
 *
 * Only providers backed by a supported Source Type are listed, and only those whose key isn't the
 * type's own spelling need to be. `paddleclassic` is intentionally omitted: Paddle Classic uses
 * the legacy `p_signature` scheme, which the `PADDLE` source type, i.e. Paddle Billing HMAC, does
 * not support. `supabase-auth-hooks` is omitted too: its samples are Auth Hooks, while the
 * `SUPABASE` source type covers Database Webhooks. Add entries here when a matching Source Type
 * ships under another name.
 */
export const CONSOLE_PROVIDER_TO_SOURCE_TYPE: { [provider: string]: SourceType } = {
  adyen: 'ADYEN',
  bigcommerce: 'BIGCOMMERCE',
  bitbucket: 'BITBUCKET',
  chargebee: 'CHARGEBEE_BILLING',
  checkout: 'CHECKOUT_COM',
  'claude-managed-agents': 'CLAUDE',
  commercelayer: 'COMMERCELAYER',
  github: 'GITHUB',
  gitlab: 'GITLAB',
  hubspot: 'HUBSPOT',
  huggingface: 'HUGGING_FACE',
  monday: 'MONDAY',
  ordinal: 'ORDINAL',
  paddlebilling: 'PADDLE',
  paypal: 'PAYPAL',
  pipedrive: 'PIPEDRIVE',
  ringcentral: 'RING_CENTRAL',
  scrapfly: 'SCRAPFLY',
  shopify: 'SHOPIFY',
  stripe: 'STRIPE',
  woocommerce: 'WOOCOMMERCE',
};

/**
 * Inverse of CONSOLE_PROVIDER_TO_SOURCE_TYPE (Source Type → canonical Console provider key).
 * 1:1 because every provider above maps to a distinct Source Type.
 */
export const SOURCE_TYPE_TO_CONSOLE_PROVIDER: Partial<Record<SourceType, string>> =
  Object.entries(CONSOLE_PROVIDER_TO_SOURCE_TYPE).reduce(
    (acc, [provider, source_type]) => {
      acc[source_type] = provider;
      return acc;
    },
    {} as Partial<Record<SourceType, string>>,
  );

/**
 * Samples providers whose webhooks belong to no Source Type, though a key or name could suggest
 * one: Paddle Classic and Supabase Auth Hooks (see above), and Coinbase Commerce, which isn't the
 * Coinbase Developer Platform that `COINBASE` covers.
 */
export const SAMPLES_PROVIDERS_WITHOUT_SOURCE_TYPE: readonly string[] = [
  'coinbase-commerce',
  'paddleclassic',
  'supabase-auth-hooks',
];

/**
 * The Source Type a samples provider's webhooks belong to: the one listed above, or else the
 * type spelled like its key (`aws-sns` is `AWS_SNS`). Null when that type isn't one
 * `is_source_type` knows, so a type that hasn't shipped yet is never named.
 */
export function sourceTypeForSamplesProvider(
  provider: string,
  is_source_type: (type: string) => boolean,
): SourceType | null {
  if (SAMPLES_PROVIDERS_WITHOUT_SOURCE_TYPE.includes(provider)) return null;
  const type = Object.prototype.hasOwnProperty.call(CONSOLE_PROVIDER_TO_SOURCE_TYPE, provider)
    ? CONSOLE_PROVIDER_TO_SOURCE_TYPE[provider]
    : provider.toUpperCase().replace(/-/g, '_');
  return is_source_type(type) ? (type as SourceType) : null;
}
